import {
  memo,
  useCallback,
  useMemo,
  useState,
  useContext,
  useEffect,
} from 'react';
import {
  ReactFlow,
  Background,
  MiniMap,
  Controls,
  BackgroundVariant,
  applyEdgeChanges,
  applyNodeChanges,
  addEdge,
  Panel,
  useReactFlow,
} from '@xyflow/react';
import {
  NODES_TYPES,
  EDGES_TYPES,
  HANDLES_TYPES,
  SOURCES_NODE_ALLOWED,
  FORBIDDEN_HANDLES_CONNECTION,
  CLEAR_SELECTED_NODE_EVENT,
  DEFAULT_DATA_FOR_NODES,
  AVAILABLE_FOR_CONNECT_NODES,
} from '../types';
import { buildHandleId } from '../lib/utils';
import { ActionPreview } from './actions-preview/ActionPreview';
import EventNode from './nodes/EventNode';
import ConditionNode from './nodes/ConditionNode';
import ActionNode from './nodes/ActionNode';
import TargetNode from './nodes/TargetNode';
import DeletableEdge from './DeletableEdge';
import EventLinkNode from './nodes/EventLinkNode';
import CollectibleNode from './nodes/CollectibleNode';
import DestroyableNode from './nodes/DestroyableNode';
import QuestNode from './nodes/QuestNode';
import SideDrawer from './SideDrawer';
import styles from '../timeline-node-manager.module.css';
import '@xyflow/react/dist/style.css';
import HeaderControls from './HeaderControls';
import { ThemeContext } from '../../../../../pages/_app';
import { v4 as uuidv4 } from 'uuid';

type NodesConstructorProps = {
  selectableAssets: any[];
  edges: any[];
  nodes: any[];
  setNodes: React.Dispatch<React.SetStateAction<any>>;
  setEdges: React.Dispatch<React.SetStateAction<any>>;
  saveTimeline: () => void;
  deleteTimeline: () => void;
  hasChanges: boolean;
  loading: boolean;
};

function NodesConstructor({
  selectableAssets,
  edges,
  nodes,
  setNodes,
  setEdges,
  saveTimeline,
  deleteTimeline,
  hasChanges,
  loading,
}: NodesConstructorProps) {
  const [isOpenSideDrawer, setIsOpenSideDrawer] = useState<boolean>(false);
  const [selectedNode, setSelectedNode] = useState<any | null>(null);
  const [selectedNodeId, setSelectedNodeId] = useState<string | null>(null);
  const [selectedEdgeId, setSelectedEdgeId] = useState<string | null>(null);
  const { isDark } = useContext(ThemeContext);
  const { getViewport } = useReactFlow();

  const connectManuallyNodes = (
    sourceId: string,
    targetId: string,
    sourceHandleId: string,
    targetHandleId: string,
    newNodeAsTarget
  ) => {
    const connection = {
      source: sourceId,
      target: targetId,
      sourceHandle: sourceHandleId,
      targetHandle: targetHandleId,
    };
    onConnect(connection, newNodeAsTarget);
  };

  const addNodeAtCenter = useCallback(
    (type: NODES_TYPES) => {
      const viewport = getViewport();

      const container = document.querySelector('.react-flow__viewport');
      const containerRect = container?.getBoundingClientRect();

      const getMaxZIndex = (existingNodes: any[]) => {
        return existingNodes.reduce((max, node) => {
          return Math.max(max, node.zIndex || 0);
        }, 0);
      };

      let centerX = -viewport.x / viewport.zoom;
      let centerY = -viewport.y / viewport.zoom;

      if (containerRect) {
        centerX = (-viewport.x + containerRect.width / 2) / viewport.zoom;
        centerY = (-viewport.y + containerRect.height / 2) / viewport.zoom;
      }

      const offsetX = (Math.random() - 0.5) * 100;
      const offsetY = (Math.random() - 0.5) * 100;
      let newNode;
      setNodes((nds) => {
        const existingNodes = Array.isArray(nds) ? nds : [];
        const maxZIndex = getMaxZIndex(existingNodes);

        newNode = {
          id: uuidv4(),
          position: { x: centerX + offsetX, y: centerY + offsetY },
          data: DEFAULT_DATA_FOR_NODES[type] || {},
          type: type,
          zIndex: maxZIndex + 1,
        };
        return [...existingNodes, newNode];
      });

      const availableForPreconnect = selectedNode
        ? AVAILABLE_FOR_CONNECT_NODES[selectedNode.type]
        : [];

      if (availableForPreconnect.includes(newNode.type) && selectedNodeId) {
        let sourceHandleType = selectedNode.type;
        let targetHandleType = selectedNode.type;
        let idSuffix = '';

        if (
          selectedNode.type === NODES_TYPES.QUEST &&
          newNode.type === NODES_TYPES.ACTION
        ) {
          sourceHandleType = NODES_TYPES.EVENT;
          targetHandleType = NODES_TYPES.EVENT;
        }

        if (newNode.type === NODES_TYPES.EVENT_LINK) {
          sourceHandleType = `${HANDLES_TYPES.EVENT}-link`;
          targetHandleType = HANDLES_TYPES.EVENT;
        }

        if (
          (selectedNode.type === NODES_TYPES.EVENT ||
            selectedNode.type === NODES_TYPES.QUEST) &&
          newNode.type !== NODES_TYPES.EVENT_LINK
        ) {
          let itemsCount = 0;

          edges
            .filter((edge) => edge.source === selectedNodeId)
            .forEach((edge) => {
              const target = nodes.find((node) => node.id === edge.target);
              if (target.type !== NODES_TYPES.EVENT_LINK) {
                itemsCount += 1;
              }
            });

          idSuffix = `${itemsCount + 1}`;
        }

        connectManuallyNodes(
          selectedNodeId,
          newNode.id,
          buildHandleId({
            type: 'output',
            nodeId: selectedNodeId,
            handleType: sourceHandleType,
            suffix: idSuffix,
          }),
          buildHandleId({
            type: 'input',
            nodeId: newNode.id,
            handleType: targetHandleType,
          }),
          newNode
        );
      }
    },
    [getViewport, setNodes, selectedNode, selectedNodeId, edges, nodes]
  );

  const nodeTypes = useMemo(
    () => ({
      [NODES_TYPES.EVENT]: EventNode,
      [NODES_TYPES.QUEST]: QuestNode,
      [NODES_TYPES.COLLECTIBLE]: CollectibleNode,
      [NODES_TYPES.DESTROYABLE]: DestroyableNode,
      [NODES_TYPES.CONDITION]: ConditionNode,
      [NODES_TYPES.ACTION]: (props) => (
        <ActionNode {...props} selectableAssets={selectableAssets} />
      ),
      [NODES_TYPES.TARGET]: (props) => (
        <TargetNode {...props} selectableAssets={selectableAssets} />
      ),
      [NODES_TYPES.EVENT_LINK]: EventLinkNode,
    }),
    [selectableAssets]
  );

  const edgeTypes = useMemo(() => ({ deletableEdge: DeletableEdge }), []);

  const onNodesChange = useCallback(
    (changes) => setNodes((nds) => (nds ? applyNodeChanges(changes, nds) : [])),
    [setNodes]
  );

  const onEdgesChange = useCallback(
    (changes) => setEdges((eds) => (eds ? applyEdgeChanges(changes, eds) : [])),
    [setEdges]
  );

  function onValidConnect(sourceNode: any, targetNode: any) {
    const eventLink =
      sourceNode.type === NODES_TYPES.EVENT_LINK
        ? sourceNode
        : targetNode.type === NODES_TYPES.EVENT_LINK
        ? targetNode
        : null;
    if (eventLink) {
      setNodes((nodes) =>
        nodes
          ? nodes.map((n) =>
              n.id === eventLink.id
                ? {
                    ...n,
                    data: {
                      ...n.data,
                      [sourceNode.id === eventLink.id
                        ? 'targetEvent'
                        : 'sourceEvent']:
                        sourceNode.id === eventLink.id
                          ? targetNode.id
                          : sourceNode.id,
                    },
                  }
                : n
            )
          : []
      );
    }

    if (targetNode.type === NODES_TYPES.ACTION) {
      setNodes((nodes) =>
        nodes
          ? nodes.map((n) =>
              n.id === targetNode.id
                ? {
                    ...n,
                    data: {
                      ...n.data,
                      isOnComplete:
                        sourceNode.type === NODES_TYPES.COLLECTIBLE ||
                        sourceNode.type === NODES_TYPES.DESTROYABLE
                          ? true
                          : false,
                      onCompleteConditionId:
                        sourceNode.type === NODES_TYPES.COLLECTIBLE ||
                        sourceNode.type === NODES_TYPES.DESTROYABLE
                          ? sourceNode.id
                          : undefined,
                      eventId:
                        sourceNode.type === NODES_TYPES.QUEST ||
                        sourceNode.type === NODES_TYPES.EVENT
                          ? sourceNode.id
                          : undefined,
                    },
                  }
                : n
            )
          : []
      );
    }
  }

  function checkConnectionByHandlesType(
    sourceNode: any,
    targetNode: any,
    connection: any
  ) {
    const targetHandleType = connection.targetHandle;
    const forbiddenConnection = FORBIDDEN_HANDLES_CONNECTION.get(
      sourceNode.type
    );

    if (
      forbiddenConnection?.includes(
        targetHandleType.split('-').splice(0, 2).join('-')
      )
    ) {
      return false;
    }

    const eventLink =
      sourceNode.type === NODES_TYPES.EVENT_LINK
        ? sourceNode
        : targetNode.type === NODES_TYPES.EVENT_LINK
        ? targetNode
        : null;

    if (!eventLink) {
      return true;
    }

    if (
      eventLink.data.sourceEvent === sourceNode.id ||
      eventLink.data.sourceEvent === targetNode.id ||
      eventLink.data.targetEvent === targetNode.id ||
      eventLink.data.targetEvent === sourceNode.id
    ) {
      return false;
    }

    return true;
  }

  function checkConnectionByNodeType(sourceNode: any, targetNode: any) {
    const allowedSources = SOURCES_NODE_ALLOWED.get(targetNode.type);
    if (!allowedSources) {
      return false;
    }

    const isAllowed = allowedSources.includes(sourceNode.type);
    if (!isAllowed) {
      return false;
    }

    return true;
  }

  function additionalValidationEventLink(sourceNode, targetNode) {
    if (!sourceNode || !targetNode) {
      return true;
    }

    const eventLink =
      sourceNode.type === NODES_TYPES.EVENT_LINK
        ? sourceNode
        : targetNode.type === NODES_TYPES.EVENT_LINK
        ? targetNode
        : null;

    if (!eventLink) {
      return false;
    }

    function hasEventLinkInPath(currentNodeId: string, isBackward: boolean) {
      const visited = new Set();
      const stack = [{ nodeId: currentNodeId, isBackward }];

      while (stack.length > 0) {
        const current = stack.pop();
        if (!current) continue;

        const { nodeId, isBackward: isBackwardFlag } = current;
        if (visited.has(nodeId)) {
          continue;
        }
        visited.add(nodeId);

        const node = nodes.find((n) => n.id === nodeId);
        if (!node) {
          continue;
        }

        const nodeEdges = isBackwardFlag
          ? edges.filter((e) => e.target === nodeId)
          : edges.filter((e) => e.source === nodeId);

        for (const edge of nodeEdges) {
          const nextNodeId = isBackwardFlag ? edge.source : edge.target;
          const nextNode = nodes.find((n) => n.id === nextNodeId);

          if (nextNode && nextNode.type === NODES_TYPES.EVENT_LINK) {
            return true;
          }

          stack.push({ nodeId: nextNodeId, isBackward: isBackwardFlag });
        }
      }

      return false;
    }

    return (
      hasEventLinkInPath(sourceNode.id, false) ||
      hasEventLinkInPath(targetNode.id, true)
    );
  }

  const onConnect = useCallback(
    (connection, newNodeAsTarget?) => {
      const source = nodes.find((node) => node.id === connection.source);
      const target =
        newNodeAsTarget || nodes.find((node) => node.id === connection.target);

      if (!source || !target) {
        return;
      }

      if (!checkConnectionByNodeType(source, target)) {
        return;
      }

      if (!checkConnectionByHandlesType(source, target, connection)) {
        return;
      }

      if (additionalValidationEventLink(source, target)) {
        return;
      }

      onValidConnect(source, target);
      const edge = { ...connection, type: EDGES_TYPES.DELETABLE_EDGE };
      setEdges((eds) => addEdge(edge, eds));
    },
    [setEdges, nodes]
  );

  const findPathsToNode = useCallback(
    (targetNodeId: string) => {
      if (!edges || !nodes)
        return { edgeIds: new Set<string>(), nodeIds: new Set<string>() };

      const edgeIds = new Set<string>();
      const nodeIds = new Set<string>();
      const visited = new Set<string>();

      function traversePath(currentNodeId: string, isBackward: boolean) {
        if (visited.has(currentNodeId)) return;
        visited.add(currentNodeId);

        const currentNode = nodes.find((n) => n.id === currentNodeId);
        if (!currentNode) return;

        nodeIds.add(currentNodeId);

        if (isBackward && currentNode.type === NODES_TYPES.EVENT) return;

        edges.forEach((edge) => {
          if (isBackward) {
            if (edge.target === currentNodeId) {
              edgeIds.add(edge.id);
              traversePath(edge.source, true);
            }
          } else {
            if (edge.source === currentNodeId) {
              edgeIds.add(edge.id);
              traversePath(edge.target, false);
            }
          }
        });
      }

      traversePath(targetNodeId, true);

      return { edgeIds, nodeIds };
    },
    [edges, nodes]
  );

  const nodesWithSelection = useMemo(() => {
    if (!nodes) return [];

    return nodes.map((node) => ({
      ...node,
      selected: node.id === selectedNodeId,
    }));
  }, [nodes, selectedNodeId]);

  const edgesWithSelection = useMemo(() => {
    if (!edges) return [];

    const { edgeIds: highlightedEdgeIds } = selectedNodeId
      ? findPathsToNode(selectedNodeId)
      : { edgeIds: new Set<string>() };

    return edges.map((edge) => {
      const isDirectlySelected = edge.id === selectedEdgeId;
      const isInPath = highlightedEdgeIds.has(edge.id);
      return {
        ...edge,
        selected: isDirectlySelected,
        highlighted: isInPath,
        data: {
          ...edge.data,
          highlighted: isInPath,
        },
      };
    });
  }, [edges, selectedEdgeId, selectedNodeId, findPathsToNode]);

  function clearSelectedNode() {
    setSelectedNode(null);
    setSelectedNodeId(null);
  }

  useEffect(() => {
    window.addEventListener(CLEAR_SELECTED_NODE_EVENT, clearSelectedNode);

    return () => {
      window.removeEventListener(CLEAR_SELECTED_NODE_EVENT, clearSelectedNode);
    };
  }, []);

  return (
    <>
      <div
        className={styles.nodesConstructorControls}
        style={{
          borderBottom: isDark ? '1px solid #262626' : '1px solid #E3DFDF',
        }}
      >
        <HeaderControls
          saveTimeline={saveTimeline}
          deleteTimeline={deleteTimeline}
          selectedNode={selectedNode}
          isTimelineMode={true}
          setNodes={setNodes}
          addNodeAtCenter={addNodeAtCenter}
          hasChanges={hasChanges}
          loading={loading}
        />
      </div>
      <div
        className={`${styles.nodesConstructorWrapper} ${
          isDark ? styles.dark : ''
        }`}
      >
        <ReactFlow
          nodes={nodesWithSelection}
          edges={edgesWithSelection}
          onNodesChange={onNodesChange}
          onEdgesChange={onEdgesChange}
          onConnect={onConnect}
          nodeTypes={nodeTypes}
          edgeTypes={edgeTypes}
          onNodeClick={(_, node) => {
            setSelectedNode(node);
            setSelectedNodeId(node.id);
            setSelectedEdgeId(null);
            setIsOpenSideDrawer(true);
          }}
          onEdgeClick={(_, edge) => {
            setSelectedEdgeId(edge.id);
            setSelectedNodeId(null);
          }}
          onPaneClick={() => {
            setSelectedNodeId(null);
            setSelectedEdgeId(null);
          }}
          fitView
          style={{
            backgroundColor: isDark ? '#141414' : '#fff',
          }}
          proOptions={{ hideAttribution: true }}
        >
          <Controls
            position='top-left'
            style={{
              backgroundColor: isDark ? '#1f1f1f' : '#fff',
              borderColor: isDark ? '#333' : '#d9d9d9',
              color: '#000',
            }}
          />
          <MiniMap
            position='bottom-left'
            pannable
            zoomable
            style={{
              backgroundColor: isDark ? '#1f1f1f' : '#fff',
              borderColor: isDark ? '#333' : '#d9d9d9',
            }}
          />
          <Background
            variant={BackgroundVariant.Dots}
            gap={12}
            size={2}
            color={isDark ? '#333' : '#e0e0e0'}
          />
          <Panel position='top-right' style={{ margin: 0 }}>
            <SideDrawer
              isOpen={isOpenSideDrawer}
              setIsOpen={setIsOpenSideDrawer}
              setSelectedNode={setSelectedNode}
              selectedNode={selectedNode}
              selectableAssets={selectableAssets}
              nodes={nodes}
            />
          </Panel>
        </ReactFlow>
        <ActionPreview />
      </div>
    </>
  );
}

export default memo(NodesConstructor);
